import React, { Component } from 'react'
import PropTypes from 'prop-types'
import Highcharts from 'highcharts'
import Table from '../../../Table/Table.jsx'
import { BAR_CHART_COLORS } from '../../Helpers/ChartConstants.js'
import { getFixedDecimal } from '../../Helpers/ETFUtility.js'
import { formatDateStandard } from '../../../../global/formatters/string-formatter.js'

class PerformanceTotalReturnModelPortfolios extends Component {
  constructor(props) {
    super(props)

    this.state = {
      selectedTab: 'total-return-chart',
      selectedInterval: 'Monthly',
      selectedTotalReturnPerformance: this.props.totalReturnPerformance[0],
    }

    this.inceptionLabel = 'Since incept. '
  }

  componentDidMount() {
    // patch for highcharts bug with flatten call
    Highcharts.wrap(
      Highcharts.Axis.prototype,
      'getPlotLinePath',
      function (proceed) {
        let path = proceed.apply(this, Array.prototype.slice.call(arguments, 1))
        if (path) {
          path.flat = false
        }
        return path
      }
    )

    this.renderHighCharts()
  }

  parseTableData = () => {
    const { selectedTotalReturnPerformance } = this.state
    const getData = (name) =>
      selectedTotalReturnPerformance?.totalReturnPerfDetails?.find(
        (i) => i.type === name
      ) || null
    const tableData = []
    const compositeType = getData('PURE_GROSS')
    const net = getData('NET')
    if (compositeType && net) {
      compositeType.label = net.label
      compositeType.ytdNet = net.ytd
      compositeType.oneYearNet = net.oneYear
      compositeType.threeYearNet = net.threeYear
      compositeType.fiveYearNet = net.fiveYear
      compositeType.tenYearNet = net.tenYear
      compositeType.sinceIncepNet = net.sinceIncep
      compositeType.compIncepNet = net.compIncep
      compositeType.modelIncepNet = net.modelIncep
    }

    const modelType = getData('PURE_GROSS_MODEL')
    const netModel = getData('NET_MODEL')

    if (modelType && netModel) {
      modelType.label = net.label
      modelType.ytdNet = netModel.ytd
      modelType.oneYearNet = netModel.oneYear
      modelType.threeYearNet = netModel.threeYear
      modelType.fiveYearNet = netModel.fiveYear
      modelType.tenYearNet = netModel.tenYear
      modelType.sinceIncepNet = netModel.sinceIncep
      modelType.compIncepNet = netModel.compIncep
      modelType.modelIncepNet = net.modelIncep
    }

    const indexType =
      selectedTotalReturnPerformance?.totalReturnPerfDetails?.find(
        (i) => i.type.indexOf('INDEX') > -1
      ) || null
    compositeType && tableData.push(compositeType)
    modelType && tableData.push(modelType)
    indexType && tableData.push(indexType)

    return tableData
  }

  getAspect = () => ({
    columnSummaries: [
      {
        name: 'headerSummary0',
        colSpan: 3,
        content: () => '',
      },
      {
        name: 'headerSummary1',
        colSpan: 6,
        content: () => {
          return (
            <>
              Average Annual Returns as of{' '}
              {formatDateStandard(
                this.state?.selectedTotalReturnPerformance?.asOfDate
              )}
            </>
          )
        },
      },
    ],
    columns: [
      {
        name: 'returnName',
        className: 'Table-header u-textNoWrap u-alignMiddle',
        ariaLabel: 'Name',
        content: <div>Name</div>,
        cell: {
          className:
            'u-borderColorNeutralLight u-borderTop u-borderBottom0 u-borderLeft0 u-borderRight0',
          content: (fund) => (
            <p className="" style={{ minWidth: 150 }}>
              {fund.label}
            </p>
          ),
        },
      },
      {
        name: 'inceptionDate',
        className: 'Table-header u-textNoWrap u-alignMiddle',
        ariaLabel: 'Inception Date',
        content: <div>Inception Date</div>,
        cell: {
          className:
            'u-borderColorNeutralLight u-alignMiddle u-borderTop u-borderBottom0 u-borderLeft0 u-borderRight0 u-textNoWrap',
          content: (fund) => formatDateStandard(fund.incepDate),
        },
      },
      {
        name: 'grossNet',
        className: 'Table-header bordered u-alignMiddle',
        ariaLabel: '',
        content: <div></div>,
        cell: {
          className:
            'u-borderColorNeutralLight u-borderTop u-borderBottom0 u-borderLeft0 u-borderRight0 u-textNoWrap',
          padding: 'u-paddingHoriz0',
          content: (fund) =>
            fund.type.indexOf('INDEX') < 0 && (
              <>
                <div className="u-paddingBottom2gu u-paddingHoriz2gu">
                  (pure gross)
                </div>
                <div className="u-paddingTop2gu u-paddingHoriz2gu u-borderColorNeutralLight u-borderTop u-borderBottom0 u-borderLeft0 u-borderRight0">
                  (net)
                </div>
              </>
            ),
        },
      },
      {
        name: 'ytd',
        className: 'Table-header u-textRight u-textNoWrap u-alignMiddle',
        ariaLabel: 'YTD (cum.)',
        content: <div>YTD (cum.)</div>,
        cell: {
          className:
            'u-borderColorNeutralLight u-borderTop u-borderBottom0 u-borderLeft0 u-borderRight0 u-textNoWrap u-textRight',
          padding: 'u-paddingHoriz0',
          content: (fund) => (
            <>
              <div className="u-paddingBottom2gu u-paddingHoriz2gu">
                {getFixedDecimal(fund.ytd)}
              </div>
              {fund.type.indexOf('INDEX') < 0 && (
                <div className="u-paddingTop2gu u-paddingHoriz2gu u-borderColorNeutralLight u-borderTop u-borderBottom0 u-borderLeft0 u-borderRight0">
                  {getFixedDecimal(fund.ytdNet)}
                </div>
              )}
            </>
          ),
        },
      },
      {
        name: '1year',
        className: 'Table-header u-textRight u-textNoWrap u-alignMiddle',
        ariaLabel: '1 Year',
        content: <div>1 Year</div>,
        cell: {
          className:
            'u-borderColorNeutralLight u-borderTop u-borderBottom0 u-borderLeft0 u-borderRight0 u-textNoWrap u-textRight',
          padding: 'u-paddingHoriz0',
          content: (fund) => (
            <>
              <div className="u-paddingBottom2gu u-paddingHoriz2gu">
                {getFixedDecimal(fund.oneYear)}
              </div>
              {fund.type.indexOf('INDEX') < 0 && (
                <div className="u-paddingTop2gu u-paddingHoriz2gu u-borderColorNeutralLight u-borderTop u-borderBottom0 u-borderLeft0 u-borderRight0">
                  {getFixedDecimal(fund.oneYearNet)}
                </div>
              )}
            </>
          ),
        },
      },
      {
        name: '3year',
        className: 'Table-header u-textRight u-textNoWrap u-alignMiddle',
        ariaLabel: '3 Year',
        content: <div>3 Year</div>,
        cell: {
          className:
            'u-borderColorNeutralLight u-borderTop u-borderBottom0 u-borderLeft0 u-borderRight0 u-textNoWrap u-textRight',
          padding: 'u-paddingHoriz0',
          content: (fund) => (
            <>
              <div className="u-paddingBottom2gu u-paddingHoriz2gu">
                {getFixedDecimal(fund.threeYear)}
              </div>
              {fund.type.indexOf('INDEX') < 0 && (
                <div className="u-paddingTop2gu u-paddingHoriz2gu u-borderColorNeutralLight u-borderTop u-borderBottom0 u-borderLeft0 u-borderRight0">
                  {getFixedDecimal(fund.threeYearNet)}
                </div>
              )}
            </>
          ),
        },
      },
      {
        name: '5year',
        className: 'Table-header u-textRight u-textNoWrap u-alignMiddle',
        ariaLabel: '5 Year',
        content: <div>5 Year</div>,
        cell: {
          className:
            'u-borderColorNeutralLight u-borderTop u-borderBottom0 u-borderLeft0 u-borderRight0 u-textNoWrap u-textRight',
          padding: 'u-paddingHoriz0',
          content: (fund) => (
            <>
              <div className="u-paddingBottom2gu u-paddingHoriz2gu">
                {getFixedDecimal(fund.fiveYear)}
              </div>
              {fund.type.indexOf('INDEX') < 0 && (
                <div className="u-paddingTop2gu u-paddingHoriz2gu u-borderColorNeutralLight u-borderTop u-borderBottom0 u-borderLeft0 u-borderRight0">
                  {getFixedDecimal(fund.fiveYearNet)}
                </div>
              )}
            </>
          ),
        },
      },
      {
        name: '10year',
        className: 'Table-header u-textRight u-textNoWrap u-alignMiddle',
        ariaLabel: '10 Year',
        content: <div>10 Year</div>,
        cell: {
          className:
            'u-borderColorNeutralLight u-borderTop u-borderBottom0 u-borderLeft0 u-borderRight0 u-textNoWrap u-textRight',
          padding: 'u-paddingHoriz0',
          content: (fund) => (
            <>
              <div className="u-paddingBottom2gu u-paddingHoriz2gu">
                {getFixedDecimal(fund.tenYear)}
              </div>
              {fund.type.indexOf('INDEX') < 0 && (
                <div className="u-paddingTop2gu u-paddingHoriz2gu u-borderColorNeutralLight u-borderTop u-borderBottom0 u-borderLeft0 u-borderRight0">
                  {getFixedDecimal(fund.tenYearNet)}
                </div>
              )}
            </>
          ),
        },
      },
      {
        name: 'since',
        className: 'Table-header u-textRight u-alignMiddle',
        ariaLabel: 'Since Inception',
        content: <div>Since Inception</div>,
        cell: {
          className:
            'u-borderColorNeutralLight u-borderTop u-borderBottom0 u-borderLeft0 u-borderRight0 u-textNoWrap u-textRight',
          padding: 'u-paddingHoriz0',
          content: (fund) => (
            <>
              <div className="u-paddingBottom2gu u-paddingHoriz2gu">
                {fund.sinceIncep !== '-'
                  ? getFixedDecimal(fund.sinceIncep)
                  : getFixedDecimal(fund.compIncep)}
              </div>
              {fund.type.indexOf('INDEX') < 0 && (
                <div className="u-paddingTop2gu u-paddingHoriz2gu u-borderColorNeutralLight u-borderTop u-borderBottom0 u-borderLeft0 u-borderRight0">
                  {fund.sinceIncepNet !== '-'
                    ? getFixedDecimal(fund.sinceIncepNet)
                    : getFixedDecimal(fund.compIncepNet)}
                </div>
              )}
            </>
          ),
        },
      },
    ],
  })

  componentDidUpdate() {
    this.renderHighCharts()
  }

  setSelectedInterval = (event) => {
    const selectedValue = event.currentTarget.value
    this.setState({
      selectedInterval: selectedValue,
      selectedTotalReturnPerformance:
        this.getSelectedTimeInterval(selectedValue),
    })
  }

  setSelectedTab = (event) => {
    event.preventDefault()
    const { toggleViewClass } = event.currentTarget.dataset
    this.setState({
      selectedTab: toggleViewClass,
    })
  }

  getSelectedTimeInterval = (selectedValue) => {
    const { totalReturnPerformance } = this.props
    return (
      totalReturnPerformance?.find((i) => i.intervalType === selectedValue) ||
      {}
    )
  }

  getHighChartsData = () => {
    const { totalReturnPerfDetails } = this.state.selectedTotalReturnPerformance
    const graphData = []
    totalReturnPerfDetails.forEach((perfDetail) => {
      const data = {
        index: totalReturnPerfDetails?.length - 1,
        name: perfDetail.label,
        data: [
          Number(perfDetail.ytd),
          Number(perfDetail.oneYear),
          Number(perfDetail.threeYear),
          Number(perfDetail.fiveYear),
          Number(perfDetail.tenYear),
          Number(perfDetail.sinceIncep),
        ],
      }
      if (perfDetail?.type?.includes('PURE_GROSS')) {
        data.name = perfDetail.label + ' - Pure Gross'
        data.index = 0
      } else if (perfDetail?.type?.includes('NET')) {
        data.name = perfDetail.label + ' - Net'
        data.index = 1
      }
      graphData.push(data)
    })

    return graphData
  }

  renderHighCharts = () => {
    Highcharts.chart('total-return-performance-chart', {
      chart: {
        spacing: [22, 0, 10, 0],
        style: {
          fontFamily: 'Franklin Gothic Condensed',
          fontSize: '14px',
        },
        type: 'column',
      },
      colors: BAR_CHART_COLORS,
      credits: false,
      legend: {
        itemMarginBottom: 5,
        itemStyle: {
          color: 'rgb(35, 31, 32)',
          fontSize: '16px',
          lineHeight: '1em',
        },
        margin: 10,
        symbolPadding: 4,
        symbolHeight: 12,
        symbolRadius: 12,
      },
      plotOptions: {
        column: {
          borderWidth: 0,
          pointPadding: 0,
          states: {
            hover: {
              brightness: 0,
            },
          },
        },
      },
      series: this.getHighChartsData(),
      title: { text: '' },
      tooltip: {
        backgroundColor: 'rgba(255,255,255, 0.95)',
        borderColor: 'rgba(35, 31, 32, 0.3)',
        borderRadius: 5,
        footerFormat: '</table>',
        headerFormat:
          '<h4 class="u-marginBottom2gu">{point.key}</h4>' +
          '<table class="Table" style="width: 300px;">',
        padding: 10,
        pointFormat:
          '<tr class="Table-row" style="background-color: transparent;">' +
          '<td class="Table-cell u-paddingBottom0 u-paddingLeft0 u-paddingRight0 u-paddingTop2gu u-textTight" style="width: 10px;">' +
          '<hr class="Bullet" style="color: {series.color};">' +
          '</td>' +
          '<td class="Table-cell u-paddingBottom0 u-paddingLeft1gu u-paddingRight4gu u-paddingTop2gu u-textTight">' +
          '{series.name}' +
          '</td>' +
          '<td class="Table-cell u-paddingBottom0 u-paddingHoriz0 u-paddingTop2gu u-textRight u-textTight">' +
          '{point.y:.2f}%' +
          '</td>' +
          '</tr>',
        shadow: false,
        shared: true,
        useHTML: true,
      },
      xAxis: {
        categories: [
          'YTD (cum.)',
          '1-year',
          '3-year',
          '5-year',
          '10-year',
          this.inceptionLabel +
            formatDateStandard(
              this.state.selectedTotalReturnPerformance?.totalReturnPerfDetails.find(
                (i) => i.incepDate !== null && i.incepDate !== '-'
              ).incepDate
            ),
        ],
        crosshair: { color: 'rgba(35, 31, 32, 0.05)' },
        labels: {
          style: {
            color: 'rgba(35, 31, 32, 0.60)',
            fontSize: '14px',
          },
        },
        lineWidth: 0,
        tickWidth: 0,
      },
      yAxis: {
        labels: {
          format: '{value}%',
          style: {
            color: 'rgba(35, 31, 32, 0.60)',
            fontSize: '14px',
          },
        },
        gridLineColor: 'rgba(35, 31, 32, 0.10)',
        lineWidth: 0,
        minorTicks: true,
        title: { text: '' },
      },
    })
  }

  render() {
    const totalReturnPerformance = this.state.selectedTotalReturnPerformance
    const aspect = this.getAspect()
    return (
      <div
        className="u-borderColorNeutralLight u-borderBottom u-borderTop0 u-borderLeft0 u-borderRight0 u-marginBottom6gu u-paddingBottom6gu"
        id="total-return-performance"
      >
        <div className="Grid Grid--alignMiddle u-flexJustifyBetween u-marginBottom4gu">
          <div className="Content u-sizeFit u-sm-paddingBottom3gu u-sm-sizeFull">
            <h3 className="u-marginBottom0">
              Total Return Performance (%){' '}
              <span
                className="u-sm-paddingTop1gu u-sm-block"
                id="total-return-performance-title"
              >
                as of {formatDateStandard(totalReturnPerformance.asOfDate)}
              </span>
            </h3>
          </div>
          <div className="u-sizeFit u-flex u-sm-sizeFull">
            <div
              className="Button-group view-hide-show-control"
              data-parent-view-class="total-return-performance"
              data-default-view-class="total-return-chart"
              data-selected-class="is-selected"
            >
              <a
                href="#"
                className={`Button Button--secondary Button--small data-visualization-toggle chart-toggle ${
                  this.state.selectedTab === 'total-return-chart'
                    ? 'is-selected u-textColorWhite'
                    : ''
                }`}
                onClick={this.setSelectedTab}
                data-toggle-view-class="total-return-chart"
              >
                Chart
              </a>
              <a
                href="#"
                className={`Button Button--secondary Button--small data-visualization-toggle chart-toggle ${
                  this.state.selectedTab === 'total-return-table'
                    ? 'is-selected u-textColorWhite'
                    : ''
                }`}
                onClick={this.setSelectedTab}
                data-toggle-view-class="total-return-table"
              >
                Table
              </a>
            </div>
          </div>
        </div>
        <div
          id="total-return-performance-chart"
          className={`total-return-performance total-return-chart ${
            this.state.selectedTab === 'total-return-chart' ? '' : 'u-hidden'
          }`}
        ></div>
        <div
          id="total-return-performance-table"
          className={`u-overflowX total-return-performance total-return-table investment-products ${
            this.state.selectedTab === 'total-return-table' ? '' : 'u-hidden'
          }`}
        >
          <Table
            columns={aspect.columns}
            rows={this.parseTableData()}
            columnSummaries={aspect.columnSummaries}
            mode={'brand-light'}
          />
        </div>
      </div>
    )
  }
}

PerformanceTotalReturnModelPortfolios.propTypes = {
  totalReturnPerformance: PropTypes.array.isRequired,
}

export default PerformanceTotalReturnModelPortfolios
